import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { Subscription } from 'rxjs';
import { ParticipantFormComponent, ParticipantSetting } from '../../../../partials/participant-form/participant-form.component';
import { TranslocoService } from '@ngneat/transloco';

export interface OwnObserversChangeEvent {
  own_observer: boolean;
  own_observer_gem: boolean;
  hr_selection: string | null; // 'none', 'self', 'system', oder null
}

type HrDisplayMode = null | 'checkbox_own' | 'checkbox_needed' | 'dropdown';

@Component({
  selector: 'app-orderform-own-observers',
  templateUrl: './orderform-own-observers.component.html',
  styleUrls: ['./orderform-own-observers.component.scss']
})
export class PlannerOrderformOwnObserversComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('observer') observer: ParticipantFormComponent;
  @ViewChild('observer_gem') observer_gem: ParticipantFormComponent;

  @Input() form: UntypedFormGroup;
  @Input() options: { [key: string]: Array<SelectItem | SelectItemGroup> };
  @Input() texts: { [key: string]: string };
  @Input() disabled = false;

  @Output() changed = new EventEmitter<OwnObserversChangeEvent | null>();

  private subForm: UntypedFormGroup = this.fb.group({
    checks: this.fb.group({
      own_observer: new UntypedFormControl(''),
      own_observer_gem: new UntypedFormControl(''),
      hr_needed: new UntypedFormControl(true),
      hr_selection_mode: new UntypedFormControl('self'),
    }),
    data: this.fb.group({})
  });

  private subscriptions: Array<Subscription> = [];

  public showObserver = false;
  public hrObserverMode: string = 'no_hr';
  public hrDisplayMode: HrDisplayMode = null;
  public hrDropdownOptions: Array<SelectItem> = [];
  public settings: {[key: string]: ParticipantSetting} = {
    observer: {
      visible: false,
      required: false
    },
    observer_gem: {
      visible: false,
      required: false
    },
  };

  // Computed property für showObserverGem
  public get showObserverGem(): boolean {
    return this.hrDisplayMode !== null;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private helper: HelperService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.form.addControl('own_observers', this.subForm);
    
    // Initialize HR dropdown options
    this.hrDropdownOptions = [
      { value: 'self', label: this.transloco.translate('orderform.hr_selection_self') },
      { value: 'system', label: this.transloco.translate('orderform.hr_selection_system') },
      { value: 'none', label: this.transloco.translate('orderform.hr_selection_none') }
    ];
    
    this.subscriptions.push(this.subForm.get('checks').valueChanges.subscribe((value: any) => {
      this.emitChanges();
      this.settings.observer = {
        visible: value.own_observer,
        required: value.own_observer
      };
      
      // Wichtig: Validatoren sofort aktualisieren, wenn observer Component bereits existiert
      if (this.observer) {
        this.observer.setRequired(value.own_observer);
      }
      
      // Bestimme Sichtbarkeit des Participant-Formulars für HR Observer
      let showObserverGemForm = false;
      if (this.hrDisplayMode === 'checkbox_own') {
        // Fall E: Zeige Form wenn Checkbox aktiv
        showObserverGemForm = value.own_observer_gem;
      } else if (this.hrDisplayMode === 'dropdown') {
        // Fall C: Zeige Form wenn "self" ausgewählt
        showObserverGemForm = value.hr_selection_mode === 'self';
      }
      
      this.settings.observer_gem = {
        visible: showObserverGemForm,
        required: showObserverGemForm
      };
      
      // Wichtig: Validatoren sofort aktualisieren, wenn observer_gem Component bereits existiert
      if (this.observer_gem) {
        this.observer_gem.setRequired(showObserverGemForm);
      }
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled && changes.disabled.currentValue !== changes.disabled.previousValue) {
      if (changes.disabled.currentValue === true) {
        this.disable();
      } else {
        this.enable();
      }
    }
  }

  ngOnDestroy(): void {
    this.helper.unsetSubscriptions(this.subscriptions);
  }

  public setSettings(templateSettings: any, crossSettings: any): void {
    this.showObserver = templateSettings.min_observers > 0 && crossSettings.own_observer === true;
    
    // Store HR observer mode
    this.hrObserverMode = templateSettings.has_observer_gem || 'no_hr';
    const allowOwnObserverGem = crossSettings.own_observer_gem === true;
    
    // Bestimme den Display-Modus basierend auf Template + Unit Settings
    this.hrDisplayMode = this.determineHrDisplayMode(this.hrObserverMode, allowOwnObserverGem);
    
    if (!this.showObserver) {
      this.subForm.get('checks.own_observer').setValue(false);
    }
    if (!this.showObserverGem) {
      this.subForm.get('checks.own_observer_gem').setValue(false);
      this.subForm.get('checks.hr_selection_mode').setValue('self');
      this.subForm.get('checks.hr_needed').setValue(true);
    }
    
    // Wichtig: Nach dem setSettings die aktuellen Settings auf die Child-Components anwenden
    // Dies stellt sicher, dass die Validatoren korrekt gesetzt werden
    setTimeout(() => {
      const checksValue = this.subForm.get('checks').value;
      
      // Update observer settings
      if (this.observer) {
        this.observer.setRequired(checksValue.own_observer);
      }
      
      // Update observer_gem settings basierend auf dem Display-Modus
      let showObserverGemForm = false;
      if (this.hrDisplayMode === 'checkbox_own') {
        showObserverGemForm = checksValue.own_observer_gem;
      } else if (this.hrDisplayMode === 'dropdown') {
        showObserverGemForm = checksValue.hr_selection_mode === 'self';
      } else if (this.hrDisplayMode === 'checkbox_needed') {
        showObserverGemForm = false; // Bei checkbox_needed gibt es kein Formular für HR Observer
      }
      
      if (this.observer_gem) {
        this.observer_gem.setRequired(showObserverGemForm);
      }
    }, 0);
    
    this.emitChanges();
  }

  private determineHrDisplayMode(templateMode: string, allowOwnHr: boolean): HrDisplayMode {
    // Fall A: No HR
    if (templateMode === 'no_hr') {
      return null;
    }
    
    // Fall B & C: HR optional
    if (templateMode === 'hr_optional') {
      return allowOwnHr ? 'dropdown' : 'checkbox_needed';
    }
    
    // Fall D & E: HR mandatory
    if (templateMode === 'hr_mandatory') {
      return allowOwnHr ? 'checkbox_own' : null;
    }
    
    return null;
}

  public getPayload(): any {
    return {
      own_observer: this.subForm.get('checks.own_observer').value,
      own_observer_gem: this.subForm.get('checks.own_observer_gem').value,
      hr_selection: this.getHrSelection(),
      observer: (this.observer) ? this.observer.getPayload() : null,
      observer_gem: (this.observer_gem) ? this.observer_gem.getPayload() : null,
    };
  }

  public disable(): void {
    this.subForm.get('checks.own_observer').disable();
    this.subForm.get('checks.own_observer_gem').disable();
  }

  public enable(): void {
    this.subForm.get('checks.own_observer').enable();
    this.subForm.get('checks.own_observer_gem').enable();
  }

  private emitChanges(): void {
    const value = this.subForm.get('checks').value;
    this.changed.emit({
      own_observer: value.own_observer,
      own_observer_gem: value.own_observer_gem,
      hr_selection: this.getHrSelection()
    });
  }

  private getHrSelection(): string | null {
    const ownObserverGemEnabled = this.subForm.get('checks.own_observer_gem').value;
    const hrNeeded = this.subForm.get('checks.hr_needed').value;
    const selectionMode = this.subForm.get('checks.hr_selection_mode').value;
    
    switch (this.hrDisplayMode) {
      case null:
        // Fall A: no_hr -> keine Auswahl
        // Fall D: hr_mandatory ohne eigenen HR -> 'system' (automatisch)
        return this.hrObserverMode === 'hr_mandatory' ? 'system' : null;
      
      case 'checkbox_own':
        // Fall E: hr_mandatory mit eigenem HR
        return ownObserverGemEnabled ? 'self' : 'system';
      
      case 'checkbox_needed':
        // Fall B: hr_optional ohne eigenen HR
        return hrNeeded ? 'system' : 'none';
      
      case 'dropdown':
        // Fall C: hr_optional mit eigenem HR - direkter Wert
        return selectionMode || 'none';
      
      default:
        return null;
    }
  }

}
